import React from "react";
import {
    Box,
    Typography,
    Button,
    Card,
    CardContent,
    Grid,
    Divider,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    IconButton,
    Chip,
} from "@mui/material";

// Components
import Icon from "@/theme/vectors/bundle";

// Utilities
import { getRoleDisplay, getRoleColor } from "@/utils/config/userHelpers";

// Hooks
import { useTranslation } from "@/hooks/useTranslation";

const UserManagement = ({ 
    users, 
    roles, 
    newUser, 
    emailError, 
    loading, 
    onInputChange, 
    onAddUser, 
    onOpenSlideout
}) => {
    const { t } = useTranslation();

    return (
        <Card sx={{ mb: 4 }}>
            <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <Icon name="users" size={20} />
                    <Typography variant="h6" sx={{ ml: 1 }}>
                        {t('user.accessControl')}
                    </Typography>
                </Box>
                
                <Divider sx={{ mb: 3 }} />
                
                <Grid container spacing={2} sx={{ mb: 3, mt: 1 }}>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label={t('user.email')}
                            name="email"
                            value={newUser.email}
                            onChange={onInputChange}
                            variant="outlined"
                            size="small"
                            placeholder="username@gmail.com"
                            error={!!emailError}
                            helperText={emailError}
                            disabled={loading}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <FormControl fullWidth size="small">
                            <InputLabel id="role-select-label">
                                {t('user.role')}
                            </InputLabel>
                            <Select
                                labelId="role-select-label"
                                name="role_id"
                                value={newUser.role_id}
                                onChange={onInputChange}
                                label={t('user.role')}
                                disabled={loading}
                            >
                                {roles.map(role => (
                                    <MenuItem key={role.id} value={role.id}>
                                        {role.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={onAddUser}
                            fullWidth
                            sx={{ height: '40px' }}
                            disabled={loading}
                        >
                            {loading ? t('common.loading') : t('user.add')}
                        </Button>
                    </Grid>
                </Grid>

                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: "bold" }}>
                    {t('user.whitelistedUsers')} ({users.length})
                </Typography>

                {/* User List */}
                <List>
                    {users.map((user) => (
                        <ListItem
                            key={user.id}
                            divider
                            sx={{
                                py: 1.5,
                                borderRadius: 1,
                                mb: 1,
                                bgcolor: 'background.paper',
                                '&:hover': {
                                    bgcolor: 'rgba(0, 0, 0, 0.04)',
                                }
                            }}
                        >
                            <ListItemText
                                primary={
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        {user.is_active === 1 ? (
                                            <Box 
                                                sx={{ 
                                                    width: 8, 
                                                    height: 8, 
                                                    borderRadius: '50%', 
                                                    bgcolor: '#f7cb45', 
                                                    mr: 1,
                                                    boxShadow: '0 0 4px rgba(247, 203, 69, 0.8)'
                                                }} 
                                            />
                                        ) : (
                                            <Box 
                                                sx={{ 
                                                    width: 8, 
                                                    height: 8, 
                                                    borderRadius: '50%', 
                                                    bgcolor: '#9e9e9e', 
                                                    mr: 1,
                                                    boxShadow: '0 0 4px rgba(158, 158, 158, 0.8)'
                                                }} 
                                            />
                                        )}
                                        <Typography variant="body1">{user.email}</Typography>
                                    </Box>
                                }
                                secondary={
                                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                                        <Chip
                                            label={getRoleDisplay(user.role_id, roles)}
                                            size="small"
                                            sx={{ 
                                                height: 24, 
                                                backgroundColor: getRoleColor(user.role_id),
                                                color: '#fff',
                                                fontWeight: 'medium',
                                                fontSize: '0.75rem'
                                            }}
                                        />
                                    </Box>
                                }
                            />
                            <ListItemSecondaryAction>
                                <Box sx={{ display: 'flex' }}>
                                    <IconButton 
                                        aria-label="edit"
                                        onClick={() => onOpenSlideout(user)}
                                        size="small"
                                        sx={{
                                            bgcolor: 'rgba(0, 0, 0, 0.03)',
                                            color: 'text.secondary',
                                            '&:hover': {
                                                bgcolor: 'rgba(25, 118, 210, 0.08)',
                                                color: 'primary.main'
                                            },
                                            border: '1px solid rgba(0, 0, 0, 0.08)',
                                            width: 28,
                                            height: 28
                                        }}
                                        disabled={loading}
                                    >
                                        <Icon name="edit" size={14} />
                                    </IconButton>
                                </Box>
                            </ListItemSecondaryAction>
                        </ListItem>
                    ))}
                </List>
            </CardContent>
        </Card>
    );
};

export default UserManagement;